import React, { useState, useCallback } from 'react';
import {
  LayoutGrid, Layers, ChevronDown, ChevronRight,
  PanelLeftClose, PanelLeft, Film, FileText, RefreshCw
} from 'lucide-react';
import { useSiteContext } from '../../context/SiteContext';
import { useMeetingState } from '../../context/MeetingStateContext';
import { logContractAuditEvent } from '../../services/contractAuditService';
import { usePubSub, useMeeting } from '../../config/sdkConfig';
import SceneManager from './streaming/SceneManager';
import OverlayManager from './streaming/OverlayManager';
// import AINotetakerPanel from './AINotetakerPanel'; // Hidden/Disabled
import { ElementsPanel, ElementFolderModal } from './elements';
import { CTAModal } from './cta';
import { PresentationElementModal } from './presentation';
import { PollModal } from './poll';
import { TimerElementModal } from './timer';
import { VideoElementModal } from './video';
import { AudioElementModal } from './audio';
import { PrompterElementModal } from './prompter';
import { WebOverlayModal } from './weboverlay';
import { TalkingPointModal } from './talkingpoint';
import { LeadCaptureModal } from './leadcapture';
import { ProductShowcaseModal } from './product';
import { CheckoutModal } from './checkout';
import { AuctionModal } from './auction';
import { ContractModal } from './contract';
import { AssetLibraryModal } from './assets';
import CopyToMyElementsModal from '../elements/CopyToMyElementsModal';
import CloneAllSharedElementsModal from '../elements/CloneAllSharedElementsModal';
import ShareElementModal from '../elements/ShareElementModal';
import ShareFolderModal from '../elements/ShareFolderModal';
import GetSharedElementModal from '../elements/GetSharedElementModal';
import folderService from '../../services/folderServices';
import pollServices from '../../services/pollServices';
import requests from '../../services/httpServices';
import { toast } from 'react-hot-toast';

const layouts = [
  { id: 'gallery', name: 'Gallery', icon: '◫◫' },
  { id: 'focus', name: 'Focus', icon: '▢' },
  { id: 'speaker-top', name: 'Speaker Top', icon: '▬' },
  { id: 'speaker-side', name: 'Speaker Side', icon: '◧' },
];

export default function StudioLeftSidebar({
  isCollapsed,
  onToggleCollapse,
  selectedLayout,
  onLayoutChange,
  showElementSection,
  setShowElementSection,
  // New props for elements system
  elementFolders = [],
  elements = [],
  activeElements = {},
  onActivateElement,
  onDeactivateElement,
  onRefreshElements,
  activeContractView,
  setActiveContractView,
}) {
  const [expandedSections, setExpandedSections] = useState(['elements']);
  const [scenesTab, setScenesTab] = useState('scenes'); // 'scenes' or 'overlays'
  const [currentScene, setCurrentScene] = useState('scene-1');
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [showCTAModal, setShowCTAModal] = useState(false);
  const [editingCTA, setEditingCTA] = useState(null);
  const [showPresentationModal, setShowPresentationModal] = useState(false);
  const [editingPresentation, setEditingPresentation] = useState(null);
  const [showPollModal, setShowPollModal] = useState(false);
  const [editingPoll, setEditingPoll] = useState(null);
  const [showTimerModal, setShowTimerModal] = useState(false);
  const [editingTimer, setEditingTimer] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [editingAudio, setEditingAudio] = useState(null);
  const [showPrompterModal, setShowPrompterModal] = useState(false);
  const [editingPrompter, setEditingPrompter] = useState(null);
  const [showWebOverlayModal, setShowWebOverlayModal] = useState(false);
  const [editingWebOverlay, setEditingWebOverlay] = useState(null);
  const [showTalkingPointModal, setShowTalkingPointModal] = useState(false);
  const [editingTalkingPoint, setEditingTalkingPoint] = useState(null);
  const [showLeadCaptureModal, setShowLeadCaptureModal] = useState(false);
  const [editingLeadCapture, setEditingLeadCapture] = useState(null);
  const [showProductShowcaseModal, setShowProductShowcaseModal] = useState(false);
  const [editingProductShowcase, setEditingProductShowcase] = useState(null);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [editingCheckout, setEditingCheckout] = useState(null);
  const [showAuctionModal, setShowAuctionModal] = useState(false);
  const [editingAuction, setEditingAuction] = useState(null);
  const [showContractModal, setShowContractModal] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [showAssetLibrary, setShowAssetLibrary] = useState(false);
  const [copyingSharedElement, setCopyingSharedElement] = useState(null);
  const [cloningSharedFolder, setCloningSharedFolder] = useState(null);
  const [sharingElement, setSharingElement] = useState(null);
  const [sharingFolder, setSharingFolder] = useState(null);
  const [showGetSharedModal, setShowGetSharedModal] = useState(false);
  const [focusFolderId, setFocusFolderId] = useState(null);
  const [isRefreshingElements, setIsRefreshingElements] = useState(false);
  const { folderElementList, setFolderElementList, setPollResults, setShowPollResults, setPrompterElement, meetingSession, user } = useSiteContext();
  const isBasicPlanUser = user?.plan !== 'Business';
  const BASIC_PLAN_ALLOWED_TYPES = ['video', 'audio', 'presentation'];
  const getSharedCopyHandler = (element) => {
    if (!element) return undefined;
    if (isBasicPlanUser && !BASIC_PLAN_ALLOWED_TYPES.includes(element.type)) return undefined;
    return () => handleOpenCopySharedElement(element);
  };
  const mMeeting = useMeeting();
  const { publish } = usePubSub('SHOW_ELEMENT_SECTION');
  const { publish: publishPoll } = usePubSub('POLL');
  const { publish: publishTimer } = usePubSub('TIMER');
  const { publish: publishPrompter } = usePubSub('PROMPTER');
  const { publish: publishPresentation } = usePubSub('PRESENTATION');
  const { publish: publishLeadCapture } = usePubSub('LEAD_CAPTURE');
  const meetingState = useMeetingState();

  const handleOpenCopySharedElement = (element) => {
    // Close any open edit modal so only the copy modal is visible
    setShowCTAModal(false);
    setShowPresentationModal(false);
    setShowPollModal(false);
    setShowTimerModal(false);
    setShowVideoModal(false);
    setShowAudioModal(false);
    setShowPrompterModal(false);
    setShowWebOverlayModal(false);
    setShowTalkingPointModal(false);
    setShowLeadCaptureModal(false);
    setShowProductShowcaseModal(false);
    setShowCheckoutModal(false);
    setShowAuctionModal(false);
    setShowContractModal(false);
    setCopyingSharedElement(element);
  };

  // Transform folderElementList to the format expected by ElementsPanel
  // Split into own folders and shared folders first
  const ownFolderList = (folderElementList || []).filter(f => !f.shared);
  const sharedFolderList = (folderElementList || []).filter(f => f.shared);

  const transformedFolders = ownFolderList.map(folder => ({
    id: folder._id,
    name: folder.name,
    color: folder.color || '#6a1fbf',
    order: folder.sortorder || folder.order || 0,
  }));

  const transformedElements = ownFolderList.flatMap(folder =>
    (folder.elements || []).map(el => {
      // Parse settings if it's a JSON string (backend stores element data in settings)
      let parsedSettings = {};
      if (el.settings) {
        try {
          parsedSettings = typeof el.settings === 'string' ? JSON.parse(el.settings) : el.settings;
          // Handle legacy double-encoded settings: if the result has a nested
          // "settings" string (from a previous bug), parse that inner layer too
          if (parsedSettings.settings && typeof parsedSettings.settings === 'string') {
            try {
              const inner = JSON.parse(parsedSettings.settings);
              parsedSettings = { ...parsedSettings, ...inner };
              delete parsedSettings.settings;
            } catch (_) {
              // inner parse failed, keep as-is
            }
          }
        } catch (e) {
          console.error('Failed to parse element settings:', e);
        }
      }

      // Debug logging for poll elements (commented out to reduce console noise)
      // if (el.type === 'poll') {
      //   console.log('=== TRANSFORM ELEMENT DEBUG ===');
      //   console.log('transformedElements - raw element:', el);
      //   console.log('transformedElements - el.settings type:', typeof el.settings);
      //   console.log('transformedElements - parsedSettings:', parsedSettings);
      //   console.log('transformedElements - el.poll_type:', el.poll_type);
      //   console.log('transformedElements - parsedSettings.poll_type:', parsedSettings.poll_type);
      // }

      // Merge parsed settings into the element data
      // parsedSettings should contain poll_type from the backend settings JSON
      const elementData = {
        ...el,
        ...parsedSettings,
        _id: el._id,
        name: el.name,
        type: el.type,
        // Ensure poll_type is preserved from parsedSettings (where it's stored)
        poll_type: parsedSettings.poll_type || el.poll_type,
        // Ensure pollId is preserved from top-level Preset field (not in settings JSON)
        pollId: el.pollId || parsedSettings.pollId,
      };

      return {
        id: el._id,
        name: el.name,
        type: el.type,
        folder_id: folder._id,
        pollId: el.pollId || parsedSettings.pollId,
        data: elementData,
        is_favorite: el.is_favorite || false,
        order: el.sortorder || el.order || 0,
      };
    })
  ) || [];

  // Build shared folders array (same transformation as own, keeping shared/readOnly flags)
  const sharedFolders = sharedFolderList.map(folder => ({
    id: folder._id,
    name: folder.name,
    color: folder.color || '#6a1fbf',
    order: folder.sortorder || folder.order || 0,
    shared: true,
    readOnly: true,
    sharedLibraryId: folder.sharedLibraryId,
    ownerName: folder.ownerName,
    elements: (folder.elements || []).map(el => {
      let parsedSettings = {};
      if (el.settings) {
        try {
          parsedSettings = typeof el.settings === 'string' ? JSON.parse(el.settings) : el.settings;
          if (parsedSettings.settings && typeof parsedSettings.settings === 'string') {
            try {
              const inner = JSON.parse(parsedSettings.settings);
              parsedSettings = { ...parsedSettings, ...inner };
              delete parsedSettings.settings;
            } catch (_) {}
          }
        } catch (e) {
          console.error('Failed to parse shared element settings:', e);
        }
      }
      const elementData = {
        ...el,
        ...parsedSettings,
        _id: el._id,
        name: el.name,
        type: el.type,
        poll_type: parsedSettings.poll_type || el.poll_type,
        pollId: el.pollId || parsedSettings.pollId,
        shared: true,
        readOnly: true,
      };
      return {
        id: el._id,
        name: el.name,
        type: el.type,
        folder_id: folder._id,
        pollId: el.pollId || parsedSettings.pollId,
        data: elementData,
        is_favorite: el.is_favorite || false,
        order: el.sortorder || el.order || 0,
        shared: true,
        readOnly: true,
      };
    }),
  }));

  // Folder handlers
  const handleCreateFolder = () => {
    setEditingFolder(null);
    setShowFolderModal(true);
  };

  const handleEditFolder = (folder) => {
    setEditingFolder(folder);
    setShowFolderModal(true);
  };

  const handleSaveFolder = async (folderData) => {
    try {
      if (editingFolder) {
        // Update existing folder
        await folderService.updateFolder(editingFolder.id, folderData);
        toast.success('Folder updated');
      } else {
        // Create new folder
        await folderService.createFolder(folderData);
        toast.success('Folder created');
      }
      onRefreshElements?.();
    } catch (error) {
      toast.error(error.message || 'Failed to save folder');
    }
  };

  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder.name}" and all its elements?`)) return;
    try {
      await folderService.deleteFolder(folder.id);
      toast.success('Folder deleted');
      onRefreshElements?.();
    } catch (error) {
      toast.error(error.message || 'Failed to delete folder');
    }
  };

  // Element handlers
  const handleActivateElement = async (element) => {
    // For poll elements, use the POLL channel with launch type
    if (element.type === 'poll') {
      const pollData = element.data || {};
      // Check multiple locations for poll_type since it might be stored differently
      // Note: pollData.type would be 'poll' (element type), so don't use that
      let pollType = pollData.poll_type || element.poll_type || 'multiple_choice';

      // Fallback detection for open_text polls created before poll_type was properly stored
      // NOTE: Do NOT check open_text_settings — PollModal.js always includes it for all poll types
      if (pollType === 'multiple_choice') {
        const options = pollData.options || [];

        // Check for placeholder options "Response 1/2" (legacy open_text polls)
        const hasPlaceholderOptions = options.length === 2 &&
          options.every(opt => {
            const text = typeof opt === 'string' ? opt : (opt?.text || '');
            return text === 'Response 1' || text === 'Response 2';
          });

        // Check for options with empty text (created before fix when user selected open_text type)
        const hasEmptyTextOptions = options.length === 2 &&
          options.every(opt => {
            const text = typeof opt === 'string' ? opt : (opt?.text || '');
            return text === '' || text.trim() === '';
          });

        if (hasPlaceholderOptions || hasEmptyTextOptions) {
          pollType = 'open_text';
        }
      }

      const ratingSettings = pollData.rating_settings || { max_value: 5, scale_type: 'stars' };

      // Generate options based on poll type
      let normalizedOptions = [];

      if (pollType === 'rating') {
        // For rating polls, generate options based on rating scale
        // Stars = 1-5, Numbers = 1-10
        // ALWAYS use correct max_value based on scale_type (ignore stored value for consistency)
        const scaleType = ratingSettings.scale_type || 'stars';
        const maxValue = scaleType === 'numbers' ? 10 : 5;
        for (let i = 1; i <= maxValue; i++) {
          normalizedOptions.push({
            text: String(i),
            index: i - 1,
            votes: 0,
            value: i
          });
        }
      } else if (pollType === 'yes_no') {
        // For yes/no polls, generate Yes and No options
        normalizedOptions = [
          { text: 'Yes', index: 0, votes: 0, color: '#00c853' },
          { text: 'No', index: 1, votes: 0, color: '#ff6b6b' }
        ];
      } else if (pollType === 'open_text') {
        // For open text polls, no predefined options needed
        // Just use empty array - participants will type free-form responses
        normalizedOptions = [];
      } else {
        // For multiple choice/answer polls, normalize existing options
        normalizedOptions = (pollData.options || []).map((opt, idx) => {
          if (typeof opt === 'string') {
            return { text: opt, index: idx, votes: 0 };
          }
          return {
            text: opt.text || opt.label || String(opt.value || opt),
            index: typeof opt.index === 'number' ? opt.index : idx,
            votes: opt.votes || 0,
            color: opt.color
          };
        });
      }

      // Determine if multiple answers are allowed based on poll_type
      const isMultipleAnswer = pollData.poll_type === 'multiple_answer';

      // Determine auto-close settings
      // PollModal uses duration.always_visible = true to mean NO auto-close
      // So auto-close is enabled when always_visible is false
      const autoCloseEnabled = pollData.duration?.always_visible === false;
      const autoCloseSeconds = pollData.duration?.seconds || 60;

      // Normalize poll data structure for launching
      // Resolve the actual poll ID from the polls collection
      // pollId is stored as a top-level field on the Preset model AND in element.data
      let resolvedPollId = pollData.pollId || element.pollId || pollData.poll_id || element.poll_id;
      // If no pollId found on element, create a poll entry via API so votes can be persisted
      if (!resolvedPollId) {
        try {
          const pollApiData = {
            question: pollData.question || pollData.name,
            options: (pollData.options || []).map(opt =>
              typeof opt === 'string' ? opt : (opt.text || opt.label || String(opt.value || ''))
            ),
            settings: {
              shareResultsLive: pollData.results_settings?.show_immediately ?? true,
              showResultsAtEnd: pollData.results_settings?.show_after_voting ?? true,
              allowMultipleAnswers: isMultipleAnswer,
              anonymousVoting: pollData.results_settings?.anonymous_voting ?? false,
              autoCloseAfter: autoCloseEnabled,
              autoCloseSeconds: autoCloseSeconds,
            },
            status: 'active',
          };
          const pollResponse = await pollServices.createPoll(pollApiData);
          resolvedPollId = pollResponse?.data?._id || pollResponse?.data?.id || pollResponse?.data?.data?._id || pollResponse?.data?.data?.id || pollResponse?._id || pollResponse?.id;
          // Persist pollId back to the element so future launches reuse the same poll
          if (resolvedPollId && (element._id || element.id)) {
            try {
              await folderService.updateElement(element._id || element.id, {
                name: pollData.name || pollData.question,
                pollId: resolvedPollId,
              });
            } catch (persistErr) {
            }
          }
        } catch (err) {
          console.error('Failed to create poll via API:', err);
        }
      }

      // Fetch existing vote counts from the Poll document itself
      // The votePoll endpoint increments options[].votes on every vote, so the Poll has cumulative totals
      let existingVotes = [];
      let existingTotalVotes = 0;
      if (resolvedPollId) {
        try {
          const pollDoc = await pollServices.getPollById(resolvedPollId);
          const savedOptions = pollDoc?.data?.options || pollDoc?.options || [];
          // Merge saved vote counts into normalizedOptions
          savedOptions.forEach((savedOpt, idx) => {
            const voteCount = savedOpt.votes || 0;
            if (voteCount > 0 && normalizedOptions[idx]) {
              normalizedOptions[idx].votes = voteCount;
              existingTotalVotes += voteCount;
            }
          });
          // Also fetch individual voter details from MeetingPollVote for the voter list
          try {
            const meetingId = mMeeting?.meetingId;
            const sid = meetingSession;
            if (meetingId && sid) {
              const resultsResponse = await pollServices.getMeetingPollResults(meetingId, sid, resolvedPollId);
              const meetingPollVotes = resultsResponse?.data?.votes || resultsResponse?.votes || [];
              meetingPollVotes.forEach(session => {
                (session?.votes || []).forEach(v => existingVotes.push(v));
              });
            }
          } catch (_) { /* voter details are optional */ }
        } catch (err) {
        }
      }

      const pollPayload = {
        id: resolvedPollId || element.id,
        question: pollData.question || pollData.name,
        poll_type: pollType,
        options: normalizedOptions,
        appearance: pollData.appearance || {},
        results_settings: pollData.results_settings || {},
        duration: pollData.duration || {},
        rating_settings: ratingSettings,
        open_text_settings: pollData.open_text_settings || { character_limit: 500 },
        // Settings object expected by PollVotingModal
        settings: {
          shareResultsLive: pollData.results_settings?.show_immediately ?? true,
          showResultsAtEnd: pollData.results_settings?.show_after_voting ?? true,
          allowMultipleAnswers: isMultipleAnswer,
          anonymousVoting: pollData.results_settings?.anonymous_voting ?? false,
          autoCloseAfter: autoCloseEnabled,
          autoCloseSeconds: autoCloseSeconds,
        },
        votes: existingVotes,
        skipped: [],
        totalVotes: existingTotalVotes,
      };

      // Set poll results for the host to see the results panel

      // Create meeting poll vote session in database before launching
      const meetingId = mMeeting?.meetingId;
      const sessionId = meetingSession;
      // Use the resolved poll ID (already computed above)
      const pollId = resolvedPollId || pollPayload.id;

      if (meetingId && sessionId && pollId) {
        try {
          const meetingPollVoteResponse = await pollServices.createMeetingPollVote({
            meetingId,
            sessionId,
            pollId,
          });
          // Meeting poll vote session created successfully
          // Store the meeting poll vote ID for later use
          pollPayload.meetingPollVoteId = meetingPollVoteResponse?.data?._id || meetingPollVoteResponse?.data?.id || meetingPollVoteResponse?._id || meetingPollVoteResponse?.id;
        } catch (error) {
          // Continue anyway - poll will still work via PubSub
          // Continue anyway - poll will still work via PubSub, just won't persist
        }
      } else {
        // Missing meeting info for poll persistence - continue anyway
      }

      setPollResults(pollPayload);
      setShowPollResults(true);

      publishPoll({
        type: 'launch',
        poll: pollPayload,
        timestamp: Date.now(),
      }, { persist: true });

      // Also persist via Socket.IO meeting state for late joiners
      meetingState.emitElementAdd({
        id: pollPayload.id || pollPayload._id,
        type: 'poll',
        config: { poll: pollPayload },
        addedAt: Date.now(),
      });

      toast.success('Poll launched!');
      onActivateElement?.(element);
      return;
    }

    // For timer elements, use the TIMER channel
    if (element.type === 'timer') {
      const timerData = element.data || {};

      // Build timer payload matching TimerStageRenderer expected format
      const timerPayload = {
        id: timerData._id || element.id,
        _id: timerData._id || element.id,
        name: timerData.name,
        timer_type: timerData.timer_type || 'countdown_duration',
        config: timerData.config || {
          durationSeconds: 300,
          size: 'medium',
          position: 'bottom_right',
          format: 'MM_SS',
          autoStartOnShow: false,
          loopOnZero: false,
          actionOnZero: 'stop',
          theme: {
            backgroundColor: '#6a1fbf',
            textColor: '#ffffff',
            borderColor: '#ffffff',
            transparentBackground: false,
          }
        },
        position: timerData.config?.position || timerData.position || 'bottom_right',
      };

      publishTimer({
        type: 'launch',
        timer: timerPayload,
        state: { status: 'idle' },
        timestamp: Date.now(),
      }, { persist: true });

      // Also persist via Socket.IO meeting state for late joiners
      meetingState.emitElementAdd({
        id: timerPayload.id || timerPayload._id,
        type: 'timer',
        config: { timer: timerPayload, state: { status: 'idle' } },
        addedAt: Date.now(),
      });

      toast.success('Timer launched!');
      onActivateElement?.(element);
      return;
    }

    // For prompter/teleprompter elements, use the PROMPTER channel
    if (element.type === 'prompter' || element.type === 'teleprompter') {
      const prompterData = element.data || {};

      // Parse settings if it's a string (from API storage)
      let parsedSettings = prompterData.settings || {};
      if (typeof parsedSettings === 'string') {
        try {
          parsedSettings = JSON.parse(parsedSettings);
        } catch (e) {
          console.error('Failed to parse prompter settings:', e);
          parsedSettings = {};
        }
      }

      // Content can be at different levels depending on how it was saved
      const content = prompterData.content ||
                      prompterData.script ||
                      parsedSettings?.content ||
                      parsedSettings?.script ||
                      '';

      // Build prompter payload
      const prompterPayload = {
        id: prompterData._id || element.id,
        _id: prompterData._id || element.id,
        name: prompterData.name || element.name,
        content: content,
        script: content,
        settings: {
          scrollSpeed: 50,
          fontSize: 'm',
          lineHeight: 'normal',
          mirroring: false,
          highlightActiveLine: true,
          focusMode: false,
          focusLines: 3,
          countdownSeconds: 0,
          ...parsedSettings,
        },
      };

      // Set prompter element for local host display
      setPrompterElement(prompterPayload);

      // Publish to all participants
      publishPrompter({
        type: 'launch',
        prompter: prompterPayload,
        timestamp: Date.now(),
      }, { persist: true });

      // Also persist via Socket.IO meeting state for late joiners
      meetingState.emitElementAdd({
        id: prompterPayload.id || prompterPayload._id,
        type: 'prompter',
        config: { prompter: prompterPayload },
        addedAt: Date.now(),
      });

      toast.success('Teleprompter launched!');
      onActivateElement?.(element);
      return;
    }

    // For presentation elements, use the PRESENTATION channel
    if (element.type === 'presentation') {
      const presentationData = element.data || {};

      // Debug: log the element structure to see where fileUrl is stored

      // Build presentation payload - check both element and element.data for fileUrl
      const presentationPayload = {
        id: presentationData._id || element._id || element.id,
        _id: presentationData._id || element._id || element.id,
        name: presentationData.name || element.name,
        fileUrl: element.fileUrl || element.file_url || presentationData.fileUrl || presentationData.file_url || '',
        fileType: element.fileType || element.file_type || presentationData.fileType || presentationData.file_type || 'pdf',
        pageCount: element.pageCount || element.page_count || presentationData.pageCount || presentationData.page_count || 25,
        settings: presentationData.settings || element.settings || {},
      };

      publishPresentation({
        type: 'launch',
        presentation: presentationPayload,
        state: { currentSlide: 1, status: 'active' },
        timestamp: Date.now(),
      }, { persist: true });

      // Also persist via Socket.IO meeting state for late joiners
      meetingState.emitElementAdd({
        id: presentationPayload.id || presentationPayload._id,
        type: 'presentation',
        config: { presentation: presentationPayload, state: { currentSlide: 1, status: 'active' } },
        addedAt: Date.now(),
      });

      toast.success('Presentation launched!');
      onActivateElement?.(element);
      return;
    }

    // For video elements, format data to match expected structure
    if (element.type === 'video') {
      const videoData = element.data || {};

      // Debug logging

      // Build settings object expected by VideoSection
      const videoSettings = {
        file_url: videoData.file_url || '',
        fileUrl: videoData.file_url || '', // Legacy support
        display_mode: videoData.display_mode || 'fullscreen',
        playback: videoData.playback || {
          autoplay: true,
          loop: false,
          audio_mode: 'normal',
          volume: 100,
          start_at: 0,
          end_at: null,
        },
        pip_settings: videoData.pip_settings || {
          position: 'top_left',
          size: 25,
        },
      };

      // Format element for MeetingContainerNew which expects settings as stringified JSON
      const videoPayload = {
        _id: videoData._id || element.id,
        name: videoData.name || element.name,
        type: 'video',
        settings: JSON.stringify(videoSettings),
      };

      publish(JSON.stringify(videoPayload), { persist: true });

      // Also persist via Socket.IO meeting state for late joiners
      meetingState.emitElementAdd({
        id: videoPayload._id || element.id,
        type: 'video',
        config: { elementData: videoPayload },
        addedAt: Date.now(),
      });

      toast.success('Video launched!');
      onActivateElement?.(element);
      return;
    }

    // For lead capture elements, use the LEAD_CAPTURE channel
    if (element.type === 'leadcapture' || element.type === 'lead_capture') {
      const leadCaptureData = element.data || {};

      // Build lead capture payload matching LeadCaptureViewer expected format
      const leadCapturePayload = {
        id: leadCaptureData._id || element.id,
        _id: leadCaptureData._id || element.id,
        name: leadCaptureData.name || element.name,
        type: leadCaptureData.type || 'email_capture',
        formConfig: leadCaptureData.formConfig || {
          headline: 'Get Instant Access',
          subheadline: '',
          buttonText: 'Submit',
          fields: [
            { id: '1', name: 'email', label: 'Email Address', type: 'email', required: true, placeholder: 'you@example.com' }
          ],
        },
        consent: leadCaptureData.consent || { enabled: false },
        crmIntegration: leadCaptureData.crmIntegration || { enabled: false },
        branding: leadCaptureData.branding || {
          primaryColor: '#8B5CF6',
          buttonColor: '#8B5CF6',
          backgroundColor: '#1F2937',
        },
        submitBehavior: leadCaptureData.submitBehavior || {
          successMessage: 'Thank you for submitting!',
          showInlineMessage: true,
          closeFormOnSubmit: false,
        },
      };

      publishLeadCapture(JSON.stringify({
        type: 'launch',
        leadCapture: leadCapturePayload,
        timestamp: Date.now(),
      }), { persist: true });

      // Also persist via Socket.IO meeting state for late joiners
      meetingState.emitElementAdd({
        id: leadCapturePayload.id || leadCapturePayload._id,
        type: 'leadcapture',
        config: { leadCapture: leadCapturePayload },
        addedAt: Date.now(),
      });

      toast.success('Lead capture form launched!');
      onActivateElement?.(element);
      return;
    }

    // For contract elements, open locally for host preview only
    // Host must explicitly click "Send to Participants" in the ContractRenderer
    if (element.type === 'contract') {
      const contractData = element.data || {};
      const contractPayload = {
        ...contractData,
        _id: contractData._id || element.id,
        name: contractData.name || element.name,
        editable_fields: contractData.editable_fields || [],
        signatures: contractData.signatures || [],
        status: contractData.status || 'draft',
        type: 'contract',
      };
      setActiveContractView(contractPayload);
      // DON'T publish to participants yet — host fills their fields first
      onActivateElement?.(element);
      return;
    }

    // For other elements, publish via SHOW_ELEMENT_SECTION
    publish(JSON.stringify(element.data), { persist: true });

    // Also persist via Socket.IO meeting state for late joiners
    meetingState.emitElementAdd({
      id: element.data?._id || element.id,
      type: element.type || element.data?.type || 'element',
      config: { elementData: element.data },
      addedAt: Date.now(),
    });

    onActivateElement?.(element);
  };

  const handleDeactivateElement = (element) => {
    // For poll elements, end the poll but keep results visible
    if (element.type === 'poll') {
      // Keep poll results visible - don't clear them when deactivating
      // The moderator can manually close the results panel when done viewing
      // setShowPollResults(false);
      // setPollResults(null);

      // Publish end poll message to close voting for participants
      publishPoll({
        type: 'end',
        clear: true,
        pollId: element.data?._id || element.id,
        timestamp: Date.now(),
      }, { persist: true });

      // Remove from Socket.IO meeting state so late joiners don't see it
      meetingState.emitElementRemove(element.data?._id || element.id);

      onDeactivateElement?.(element);
      return;
    }

    // For timer elements, publish end timer message
    if (element.type === 'timer') {
      publishTimer({
        type: 'end',
        clear: true,
        timerId: element.data?._id || element.id,
        timestamp: Date.now(),
      }, { persist: true });

      // Remove from Socket.IO meeting state so late joiners don't see it
      meetingState.emitElementRemove(element.data?._id || element.id);

      onDeactivateElement?.(element);
      return;
    }

    // For prompter elements, clear and publish end message
    if (element.type === 'prompter' || element.type === 'teleprompter') {
      setPrompterElement(null);
      publishPrompter({
        type: 'end',
        clear: true,
        prompterId: element.data?._id || element.id,
        timestamp: Date.now(),
      }, { persist: true });

      // Remove from Socket.IO meeting state so late joiners don't see it
      meetingState.emitElementRemove(element.data?._id || element.id);

      onDeactivateElement?.(element);
      return;
    }

    // For presentation elements, publish end message
    if (element.type === 'presentation') {
      publishPresentation({
        type: 'end',
        clear: true,
        presentationId: element.data?._id || element.id,
        timestamp: Date.now(),
      }, { persist: true });

      // Remove from Socket.IO meeting state so late joiners don't see it
      meetingState.emitElementRemove(element.data?._id || element.id);

      onDeactivateElement?.(element);
      return;
    }

    // For lead capture elements, publish end message and remove from meeting state
    if (element.type === 'leadcapture' || element.type === 'lead_capture') {
      publishLeadCapture(JSON.stringify({
        type: 'end',
        clear: true,
        leadCaptureId: element.data?._id || element.id,
        timestamp: Date.now(),
      }), { persist: true });

      // Remove from Socket.IO meeting state so late joiners don't see it
      meetingState.emitElementRemove(element.data?._id || element.id);

      onDeactivateElement?.(element);
      return;
    }

    // For contract elements, close locally AND notify participants
    if (element.type === 'contract') {
      setActiveContractView(null);

      // Clear for participants
      publish(JSON.stringify({ type: 'contract', clear: true }), { persist: true });

      // Remove from Socket.IO meeting state
      meetingState.emitElementRemove(element.data?._id || element.id);

      onDeactivateElement?.(element);
      return;
    }

    // Clear the element
    publish(JSON.stringify({ type: element.type, clear: true }), { persist: true });

    // Remove from Socket.IO meeting state so late joiners don't see it
    meetingState.emitElementRemove(element.data?._id || element.id);

    onDeactivateElement?.(element);
  };

  const handleCreateElement = (type) => {
    if (type === 'cta') {
      setEditingCTA(null);
      setShowCTAModal(true);
    } else if (type === 'presentation') {
      setEditingPresentation(null);
      setShowPresentationModal(true);
    } else if (type === 'poll') {
      setEditingPoll(null);
      setShowPollModal(true);
    } else if (type === 'timer') {
      setEditingTimer(null);
      setShowTimerModal(true);
    } else if (type === 'video') {
      setEditingVideo(null);
      setShowVideoModal(true);
    } else if (type === 'audio') {
      setEditingAudio(null);
      setShowAudioModal(true);
    } else if (type === 'prompter' || type === 'teleprompter') {
      setEditingPrompter(null);
      setShowPrompterModal(true);
    } else if (type === 'weboverlay' || type === 'web_overlay') {
      setEditingWebOverlay(null);
      setShowWebOverlayModal(true);
    } else if (type === 'talkingpoint' || type === 'talking_point') {
      setEditingTalkingPoint(null);
      setShowTalkingPointModal(true);
    } else if (type === 'leadcapture' || type === 'lead_capture') {
      setEditingLeadCapture(null);
      setShowLeadCaptureModal(true);
    } else if (type === 'productshowcase' || type === 'product_showcase' || type === 'product') {
      setEditingProductShowcase(null);
      setShowProductShowcaseModal(true);
    } else if (type === 'checkout') {
      setEditingCheckout(null);
      setShowCheckoutModal(true);
    } else if (type === 'auction') {
      setEditingAuction(null);
      setShowAuctionModal(true);
    } else if (type === 'contract') {
      setEditingContract(null);
      setShowContractModal(true);
    } else {
      // For now, show a toast - full modal implementation will come with individual element ports
      toast.success(`Creating ${type} element... (Modal coming soon)`);
    }
  };

  const handleEditElement = async (element) => {
    // For shared (read-only) elements, always fetch the latest data from the
    // source so any changes the library owner made are visible immediately.
    let data = element.data;
    if (element.readOnly || element.shared) {
      try {
        const res = await requests.get(`/element/${element.id || element._id}`);
        if (res?.element) {
          const fresh = res.element;
          data = {
            ...fresh,
            ...fresh.settings,
            _id: fresh._id,
            name: fresh.name,
            type: fresh.type,
            folderId: element.folder_id,
          };
        }
      } catch (_) {
        // Fall through and use cached data
      }
    }
    // Carry readOnly flag into data so modal props like editingCTA?.readOnly work
    if (element.readOnly || element.shared) {
      data = { ...data, readOnly: true };
    }
    const el = { ...element, data };

    if (el.type === 'cta') {
      setEditingCTA({ ...el.data, folder_id: el.folder_id || el.data?.folder_id || el.data?.folderId });
      setShowCTAModal(true);
    } else if (el.type === 'presentation') {
      setEditingPresentation(el.data);
      setShowPresentationModal(true);
    } else if (el.type === 'poll') {
      setEditingPoll({ ...el.data, _id: el._id || el.id, pollId: el.pollId });
      setShowPollModal(true);
    } else if (el.type === 'timer') {
      setEditingTimer(el.data);
      setShowTimerModal(true);
    } else if (el.type === 'video') {
      setEditingVideo(el.data);
      setShowVideoModal(true);
    } else if (el.type === 'audio') {
      setEditingAudio(el.data);
      setShowAudioModal(true);
    } else if (el.type === 'prompter' || el.type === 'teleprompter') {
      setEditingPrompter(el.data);
      setShowPrompterModal(true);
    } else if (el.type === 'weboverlay' || el.type === 'web_overlay') {
      setEditingWebOverlay({ ...el.data, _id: el.id || el._id });
      setShowWebOverlayModal(true);
    } else if (el.type === 'talkingpoint' || el.type === 'talking_point') {
      setEditingTalkingPoint({ ...el.data, _id: el.id || el._id });
      setShowTalkingPointModal(true);
    } else if (el.type === 'leadcapture' || el.type === 'lead_capture') {
      setEditingLeadCapture(el.data);
      setShowLeadCaptureModal(true);
    } else if (el.type === 'productshowcase' || el.type === 'product_showcase' || el.type === 'product') {
      setEditingProductShowcase(el.data);
      setShowProductShowcaseModal(true);
    } else if (el.type === 'checkout') {
      setEditingCheckout(el.data);
      setShowCheckoutModal(true);
    } else if (el.type === 'auction') {
      setEditingAuction(el.data);
      setShowAuctionModal(true);
    } else if (el.type === 'contract') {
      setEditingContract(el.data);
      setShowContractModal(true);
    } else {
      toast.success(`Editing ${el.name}... (Modal coming soon)`);
    }
  };

  const handleSaveCTA = async (ctaData) => {
    try {
      if (editingCTA?._id) {
        // Update existing CTA
        await folderService.updateElement(editingCTA._id, {
          ...ctaData,
          type: 'cta'
        });
        toast.success('CTA updated');
      } else {
        // Create new CTA
        await folderService.createElement(ctaData.folder_id, {
          ...ctaData,
          type: 'cta'
        });
        toast.success('CTA created');
      }
      onRefreshElements?.();
    } catch (error) {
      toast.error(error.message || 'Failed to save CTA');
    }
  };

  const handleSavePresentation = async (presentationData) => {
    try {
      if (editingPresentation?._id) {
        // Update existing presentation
        await folderService.updateElement(editingPresentation._id, {
          ...presentationData,
          type: 'presentation'
        });
        toast.success('Presentation updated');
      } else {
        // Create new presentation
        await folderService.createElement(presentationData.folder_id, {
          ...presentationData,
          type: 'presentation'
        });
        toast.success('Presentation created');
      }
      onRefreshElements?.();
    } catch (error) {
      toast.error(error.message || 'Failed to save presentation');
    }
  };

  const handleSavePoll = async (pollData) => {
    try {
      // Generate options based on poll type
      let options = [];

      if (pollData.poll_type === 'rating') {
        // For rating polls, generate options based on rating scale
        // Stars = 1-5, Numbers = 1-10
        const scaleType = pollData.rating_settings?.scale_type || 'stars';
        const maxValue = pollData.rating_settings?.max_value || (scaleType === 'numbers' ? 10 : 5);
        for (let i = 1; i <= maxValue; i++) {
          options.push({
            text: scaleType === 'numbers' ? String(i) : `${i} ${i === 1 ? 'Star' : 'Stars'}`,
            index: i - 1,
            votes: 0,
            value: i
          });
        }
      } else if (pollData.poll_type === 'yes_no') {
        // For yes/no polls, generate Yes and No options
        options = [
          { text: 'Yes', index: 0, votes: 0, color: '#00c853' },
          { text: 'No', index: 1, votes: 0, color: '#ff6b6b' }
        ];
      } else if (pollData.poll_type === 'open_text') {
        // For open text polls, create placeholder options (backend requires at least 2)
        options = [
          { text: 'Response 1', index: 0, votes: 0 },
          { text: 'Response 2', index: 1, votes: 0 }
        ];
      } else {
        // For multiple choice/answer polls, use the provided options
        options = (pollData.options || []).map((opt, idx) => ({
          text: opt.text || opt,
          index: idx,
          votes: opt.votes || 0,
          color: opt.color
        }));
      }

      // Convert poll data to the format expected by pollServices API
      const pollApiData = {
        question: pollData.question,
        options: options,
        settings: {
          shareResultsLive: pollData.results_settings?.show_immediately ?? true,
          showResultsAtEnd: pollData.results_settings?.show_after_voting ?? true,
          allowMultipleAnswers: pollData.poll_type === 'multiple_answer',
          anonymousVoting: pollData.results_settings?.anonymous_voting ?? false,
          autoCloseAfter: pollData.duration?.always_visible === false,
          autoCloseSeconds: pollData.duration?.seconds || 60,
        },
        status: pollData.status || 'draft',
        // Store additional data for element system
        poll_type: pollData.poll_type,
        appearance: pollData.appearance,
        results_settings: pollData.results_settings,
        duration: pollData.duration,
        name: pollData.name,
        // Include rating_settings for rating polls
        rating_settings: pollData.rating_settings,
        // Include open_text_settings for open text polls
        open_text_settings: pollData.open_text_settings,
      };

      if (editingPoll?._id) {
        // Update existing poll in both systems
        let pollId = editingPoll.pollId;
        if (pollId) {
          // Poll exists — update it
          try {
            await pollServices.updatePoll(pollId, pollApiData);
          } catch (pollErr) {
            console.warn('Failed to update poll in polls API (non-blocking):', pollErr);
          }
        } else {
          // No pollId yet — create one and link it
          try {
            const pollResponse = await pollServices.createPoll(pollApiData);
            pollId = pollResponse?.data?._id || pollResponse?.data?.id || pollResponse?._id || pollResponse?.id;
          } catch (pollErr) {
            console.warn('Failed to create poll in polls API (non-blocking):', pollErr);
          }
        }
        // Always update in elements system - explicitly include poll_type and generated options
        await folderService.updateElement(editingPoll._id, {
          ...pollData,
          options: options, // Use generated options, not original formData.options
          type: 'poll',
          poll_type: pollData.poll_type, // Explicitly set poll_type
          pollId: pollId, // Preserve or set the pollId reference
        });
        toast.success('Poll updated');
      } else {
        // Create new poll - save to polls API first
        const pollResponse = await pollServices.createPoll(pollApiData);
        const createdPollId = pollResponse?.data?._id || pollResponse?.data?.id || pollResponse?._id || pollResponse?.id;

        // Also create in elements system with reference to poll ID
        await folderService.createElement(pollData.folder_id, {
          ...pollData,
          options: options, // Use generated options, not original formData.options
          type: 'poll',
          poll_type: pollData.poll_type, // Explicitly set poll_type
          pollId: createdPollId, // Reference to the polls API record
        });
        toast.success('Poll created');
      }
      onRefreshElements?.();
    } catch (error) {
      console.error('Error saving poll:', error);
      toast.error(error.message || 'Failed to save poll');
    }
  };

  const handleSaveTimer = async (timerData) => {
    try {
      if (editingTimer?._id) {
        // Update existing timer
        await folderService.updateElement(editingTimer._id, {
          ...timerData,
          type: 'timer'
        });
        toast.success('Timer updated');
      } else {
        // Create new timer
        await folderService.createElement(timerData.folder_id, {
          ...timerData,
          type: 'timer'
        });
        toast.success('Timer created');
      }
      onRefreshElements?.();
    } catch (error) {
      toast.error(error.message || 'Failed to save timer');
    }
  };

  const handleSaveVideo = async (videoData) => {
    try {
      if (editingVideo?._id) {
        // Update existing video
        await folderService.updateElement(editingVideo._id, {
          ...videoData,
          type: 'video'
        });
        toast.success('Video updated');
      } else {
        // Create new video
        await folderService.createElement(videoData.folder_id, {
          ...videoData,
          type: 'video'
        });
        toast.success('Video created');
      }
      onRefreshElements?.();
    } catch (error) {
      toast.error(error.message || 'Failed to save video');
    }
  };

  const handleSaveAudio = async (audioData) => {
    try {
      if (editingAudio?._id) {
        // Update existing audio
        await folderService.updateElement(editingAudio._id, {
          ...audioData,
          type: 'audio'
        });
        toast.success('Audio updated');
      } else {
        // Create new audio
        await folderService.createElement(audioData.folder_id, {
          ...audioData,
          type: 'audio'
        });
        toast.success('Audio created');
      }
      onRefreshElements?.();
    } catch (error) {
      toast.error(error.message || 'Failed to save audio');
    }
  };

  const handleSavePrompter = async (prompterData) => {
    try {
      if (editingPrompter?._id) {
        // Update existing prompter
        await folderService.updateElement(editingPrompter._id, {
          ...prompterData,
          type: 'prompter'
        });
        toast.success('Teleprompter updated');
      } else {
        // Create new prompter
        await folderService.createElement(prompterData.folder_id, {
          ...prompterData,
          type: 'prompter'
        });
        toast.success('Teleprompter created');
      }
      onRefreshElements?.();
    } catch (error) {
      toast.error(error.message || 'Failed to save teleprompter');
    }
  };

  const handleSaveWebOverlay = async (overlayData) => {
    try {
      if (editingWebOverlay?._id) {
        // Update existing web overlay
        await folderService.updateElement(editingWebOverlay._id, {
          ...overlayData,
          type: 'weboverlay'
        });
        toast.success('Web overlay updated');
      } else {
        // Create new web overlay
        await folderService.createElement(overlayData.folder_id, {
          ...overlayData,
          type: 'weboverlay'
        });
        toast.success('Web overlay created');
      }
      onRefreshElements?.();
    } catch (error) {
      toast.error(error.message || 'Failed to save web overlay');
    }
  };

  const handleSaveTalkingPoint = async (talkingPointData) => {
    try {
      if (editingTalkingPoint?._id) {
        // Update existing talking point
        await folderService.updateElement(editingTalkingPoint._id, {
          ...talkingPointData,
          type: 'talkingpoint'
        });
        toast.success('Talking point updated');
      } else {
        // Create new talking point
        await folderService.createElement(talkingPointData.folder_id, {
          ...talkingPointData,
          type: 'talkingpoint'
        });
        toast.success('Talking point created');
      }
      onRefreshElements?.();
    } catch (error) {
      toast.error(error.message || 'Failed to save talking point');
    }
  };

  const handleSaveLeadCapture = async (leadCaptureData) => {
    try {
      if (editingLeadCapture?._id) {
        // Update existing lead capture
        await folderService.updateElement(editingLeadCapture._id, {
          ...leadCaptureData,
          type: 'leadcapture'
        });
        toast.success('Lead capture form updated');
      } else {
        // Create new lead capture
        await folderService.createElement(leadCaptureData.folder_id, {
          ...leadCaptureData,
          type: 'leadcapture'
        });
        toast.success('Lead capture form created');
      }
      onRefreshElements?.();
    } catch (error) {
      toast.error(error.message || 'Failed to save lead capture form');
    }
  };

  const handleSaveProductShowcase = async (showcaseData) => {
    try {
      if (editingProductShowcase?._id) {
        // Update existing product showcase
        await folderService.updateElement(editingProductShowcase._id, {
          ...showcaseData,
          type: 'productshowcase'
        });
        toast.success('Product showcase updated');
      } else {
        // Create new product showcase
        await folderService.createElement(showcaseData.folder_id, {
          ...showcaseData,
          type: 'productshowcase'
        });
        toast.success('Product showcase created');
      }
      onRefreshElements?.();
    } catch (error) {
      toast.error(error.message || 'Failed to save product showcase');
    }
  };

  const handleSaveCheckout = async (checkoutData) => {
    try {
      if (editingCheckout?._id) {
        // Update existing checkout
        await folderService.updateElement(editingCheckout._id, {
          ...checkoutData,
          type: 'checkout'
        });
        toast.success('Checkout updated');
      } else {
        // Create new checkout
        await folderService.createElement(checkoutData.folder_id, {
          ...checkoutData,
          type: 'checkout'
        });
        toast.success('Checkout created');
      }
      onRefreshElements?.();
    } catch (error) {
      toast.error(error.message || 'Failed to save checkout');
    }
  };

  const handleSaveAuction = async (auctionData) => {
    try {
      if (editingAuction?._id) {
        // Update existing auction
        await folderService.updateElement(editingAuction._id, {
          ...auctionData,
          type: 'auction'
        });
        toast.success('Auction updated');
      } else {
        // Create new auction
        await folderService.createElement(auctionData.folder_id, {
          ...auctionData,
          type: 'auction'
        });
        toast.success('Auction created');
      }
      onRefreshElements?.();
    } catch (error) {
      toast.error(error.message || 'Failed to save auction');
    }
  };

  const handleSaveContract = async (contractData) => {
    try {
      if (editingContract?._id) {
        // Update existing contract
        await folderService.updateElement(editingContract._id, {
          ...contractData,
          type: 'contract'
        });
        toast.success('Contract updated');
      } else {
        // Create new contract
        const created = await folderService.createElement(contractData.folder_id, {
          ...contractData,
          type: 'contract'
        });
        toast.success('Contract created');

        // Audit: document created (library-scope — meetingId intentionally empty)
        const newId =
          created?._id || created?.id || created?.data?._id || created?.data?.id;
        if (newId) {
          logContractAuditEvent({
            contractElementId: String(newId),
            meetingId: '',
            eventType: 'created',
            actor: { name: 'Host', role: 'host' },
            metadata: {
              name: contractData?.name || '',
              fileUrl: contractData?.file_url || '',
            },
          });
        }
      }
      onRefreshElements?.();
    } catch (error) {
      toast.error(error.message || 'Failed to save contract');
    }
  };

  const handleDuplicateElement = async (element) => {
    try {
      // Create a copy with a new name
      const newElement = {
        ...element.data,
        name: `${element.name} (Copy)`,
      };
      await folderService.createElement(element.folder_id, newElement);
      toast.success('Element duplicated');
      onRefreshElements?.();
    } catch (error) {
      toast.error(error.message || 'Failed to duplicate element');
    }
  };

  const handleDeleteElement = async (element) => {
    if (!window.confirm(`Delete "${element.name}"?`)) return;
    try {
      await folderService.deleteElement(element.id);
      toast.success('Element deleted');
      onRefreshElements?.();
    } catch (error) {
      toast.error(error.message || 'Failed to delete element');
    }
  };

  const handleMoveElement = async (element, newFolderId) => {
    try {
      await folderService.moveElement(element.id, newFolderId);
      toast.success('Element moved');
      onRefreshElements?.();
    } catch (error) {
      toast.error(error.message || 'Failed to move element');
    }
  };

  const handleToggleFavorite = async (element) => {
    try {
      await folderService.updateElement(element.id, {
        is_favorite: !element.is_favorite
      });
      onRefreshElements?.();
    } catch (error) {
      toast.error(error.message || 'Failed to update favorite');
    }
  };

  const handleRenameElement = async (element, newName) => {
    try {
      await folderService.updateElement(element.id, { name: newName });
      toast.success('Element renamed');
      onRefreshElements?.();
    } catch (error) {
      toast.error(error.message || 'Failed to rename element');
    }
  };

  const handleReorderElements = async (reorderedElements) => {
    try {
      await folderService.reorderElements(reorderedElements);
      onRefreshElements?.();
    } catch (error) {
      toast.error(error.message || 'Failed to reorder elements');
    }
  };

  const handleReorderFolders = async (reorderedFolders) => {
    // Optimistic update — reorder folderElementList in place so UI doesn't snap back
    const orderMap = {};
    reorderedFolders.forEach(f => { orderMap[f.id] = f.order; });
    const updatedList = (folderElementList || []).map(f => {
      const id = f._id || f.id;
      if (orderMap[id] !== undefined) {
        return { ...f, sortorder: orderMap[id] };
      }
      return f;
    });
    updatedList.sort((a, b) => (a.sortorder || 0) - (b.sortorder || 0));
    setFolderElementList(updatedList);

    try {
      await folderService.reorderFolders(reorderedFolders);
    } catch (error) {
      toast.error(error.message || 'Failed to reorder folders');
      onRefreshElements?.(); // revert on error
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? [] // Close if already open
        : [section] // Open only this section, close all others
    );
  };

  const allSections = [
    { id: 'views', label: 'Views', icon: LayoutGrid, color: 'text-gray-400' },
    { id: 'elements', label: 'Elements', icon: Layers, color: 'text-[#6a1fbf]' },
    // { id: 'notetaker', label: 'AI Notetaker', icon: FileText, color: 'text-[#00c853]' }, // Hidden/Disabled
    // { id: 'scenes', label: 'Scenes & Overlays', icon: Film, color: 'text-[#f59e0b]' }, // Hidden
  ];

  if (isCollapsed) {
    return (
      <div className="w-10 md:w-14 bg-[#001233]/95 backdrop-blur-xl border-r border-white/10 flex flex-col h-full">
        <button
          onClick={onToggleCollapse}
          className="p-2.5 md:p-4 hover:bg-white/10 transition-colors border-b border-white/10"
        >
          <PanelLeft className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
        </button>
        <div className="flex-1 flex flex-col items-center py-3 md:py-4 gap-1.5 md:gap-2">
          {allSections.map((section) => (
            <button
              key={section.id}
              onClick={() => {
                onToggleCollapse();
                setExpandedSections([section.id]);
              }}
              className="p-2 md:p-3 rounded-xl hover:bg-white/10 transition-colors"
              title={section.label}
            >
              <section.icon className={`w-4 h-4 md:w-5 md:h-5 ${section.color || 'text-gray-400'}`} />
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-64 md:w-[380px] bg-[#001233]/95 backdrop-blur-xl border-r border-white/10 flex flex-col h-full">
      <style>{`
        .custom-purple-scroll::-webkit-scrollbar {
          width: 12px;
        }
        .custom-purple-scroll::-webkit-scrollbar-track {
          background: #000000;
        }
        .custom-purple-scroll::-webkit-scrollbar-thumb {
          background: #6a1fbf;
          border-radius: 6px;
          box-shadow: 0 0 20px rgba(106, 31, 191, 0.5);
        }
        .custom-purple-scroll::-webkit-scrollbar-thumb:hover {
          background: #8b3fd6;
          box-shadow: 0 0 24px rgba(106, 31, 191, 0.7);
        }
        .custom-purple-scroll {
          scrollbar-width: thin;
          scrollbar-color: #6a1fbf #000000;
        }
      `}</style>

      {/* Header with collapse button */}
      <div className="p-3 border-b border-white/10 flex items-center justify-between">
        <span className="text-sm font-medium text-white">Studio Controls</span>
        <button
          onClick={onToggleCollapse}
          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
        >
          <PanelLeftClose className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {/* Sections */}
      <div className="flex-1 overflow-y-auto custom-purple-scroll">
        <div className="p-2">
          {/* Views Section */}
          <div className="mb-1">
            <button
              onClick={() => toggleSection('views')}
              className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <LayoutGrid className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium text-white">Views</span>
              </div>
              <ChevronDown
                className={`w-4 h-4 text-gray-400 transition-transform ${
                  expandedSections.includes('views') ? 'rotate-180' : ''
                }`}
              />
            </button>

            {expandedSections.includes('views') && (
              <div className="px-3 py-2">
                <p className="text-xs text-gray-400 mb-3">Video Layouts</p>
                <div className="grid grid-cols-2 gap-2">
                  {layouts.map(layout => (
                    <button
                      key={layout.id}
                      onClick={() => onLayoutChange(layout.id)}
                      className={`p-3 rounded-lg border transition-all ${
                        selectedLayout === layout.id
                          ? 'border-[#00c853] bg-gradient-to-r from-[#6a1fbf]/20 to-[#00c853]/20'
                          : 'border-white/10 hover:border-white/30 bg-white/5'
                      }`}
                    >
                      <div className={`flex justify-center mb-1 text-xl ${
                        selectedLayout === layout.id ? 'text-[#00c853]' : 'text-gray-400'
                      }`}>
                        {layout.icon}
                      </div>
                      <div className="text-xs text-white text-center">{layout.name}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="my-3 border-t border-white/10" />

          {/* Elements Section */}
          <div className="mb-1">
            <div
              onClick={() => toggleSection('elements')}
              className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-white/5 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <Layers className="w-4 h-4 text-[#6a1fbf]" />
                <span className="text-sm font-medium text-white">Elements</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (isRefreshingElements || !onRefreshElements) return;
                    setIsRefreshingElements(true);
                    try { await onRefreshElements(); } finally { setIsRefreshingElements(false); }
                  }}
                  disabled={isRefreshingElements || !onRefreshElements}
                  className="p-1 text-gray-400 hover:text-white hover:bg-white/10 rounded-md transition-colors disabled:opacity-50"
                  title="Refresh elements"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isRefreshingElements ? 'animate-spin' : ''}`} />
                </button>
                <ChevronDown
                  className={`w-4 h-4 text-gray-400 transition-transform ${
                    expandedSections.includes('elements') ? 'rotate-180' : ''
                  }`}
                />
              </div>
            </div>

            {expandedSections.includes('elements') && (
              <div className="max-h-[calc(100vh-450px)] min-h-[200px] overflow-y-auto custom-purple-scroll">
                <ElementsPanel
                  folders={transformedFolders}
                  elements={transformedElements}
                  activeElements={activeElements}
                  onActivateElement={handleActivateElement}
                  onDeactivateElement={handleDeactivateElement}
                  onCreateFolder={handleCreateFolder}
                  onEditFolder={handleEditFolder}
                  onDeleteFolder={handleDeleteFolder}
                  onCreateElement={handleCreateElement}
                  onEditElement={handleEditElement}
                  onDuplicateElement={handleDuplicateElement}
                  onDeleteElement={handleDeleteElement}
                  onMoveElement={handleMoveElement}
                  onToggleFavorite={handleToggleFavorite}
                  hiddenTypesExtra={!user?.isAdmin ? ['productshowcase', 'product_showcase', 'auction'] : []}
                  onRenameElement={handleRenameElement}
                  onReorderElements={handleReorderElements}
                  onReorderFolders={handleReorderFolders}
                  onOpenAssetLibrary={() => setShowAssetLibrary(true)}
                  sharedFolders={sharedFolders}
                  onCopySharedElement={handleOpenCopySharedElement}
                  onCloneAllSharedElements={(folder) => setCloningSharedFolder(folder)}
                  onShareElement={(element) => setSharingElement(element)}
                  onShareFolder={(folder) => setSharingFolder(folder)}
                  onGetSharedElement={() => setShowGetSharedModal(true)}
                  getSharedLabel="Add Shared"
                  focusFolderId={focusFolderId}
                  onFocusFolderHandled={() => setFocusFolderId(null)}
                  onRefreshElements={onRefreshElements}
                />
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="my-3 border-t border-white/10" />

          {/* AI Notetaker Section - Hidden/Disabled
          <div className="mb-1">
            <button
              onClick={() => toggleSection('notetaker')}
              className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <FileText className="w-4 h-4 text-[#00c853]" />
                <span className="text-sm font-medium text-white">AI Notetaker</span>
              </div>
              <ChevronDown
                className={`w-4 h-4 text-gray-400 transition-transform ${
                  expandedSections.includes('notetaker') ? 'rotate-180' : ''
                }`}
              />
            </button>

            {expandedSections.includes('notetaker') && (
              <div className="px-3 py-2">
                <AINotetakerPanel />
              </div>
            )}
          </div>

          {/* Divider */}
          {/* <div className="my-3 border-t border-white/10" /> */}

          {/* Scenes & Overlays Section - Hidden */}
          {/* <div className="mb-1">
            <button
              onClick={() => toggleSection('scenes')}
              className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Film className="w-4 h-4 text-[#f59e0b]" />
                <span className="text-sm font-medium text-white">Scenes & Overlays</span>
              </div>
              <ChevronDown
                className={`w-4 h-4 text-gray-400 transition-transform ${
                  expandedSections.includes('scenes') ? 'rotate-180' : ''
                }`}
              />
            </button>

            {expandedSections.includes('scenes') && (
              <div className="px-3 py-2">
                <div className="flex bg-white/5 rounded-lg p-1 mb-3">
                  <button
                    onClick={() => setScenesTab('scenes')}
                    className={`flex-1 text-xs py-1.5 px-3 rounded-md transition-colors ${
                      scenesTab === 'scenes'
                        ? 'bg-white/10 text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Scenes
                  </button>
                  <button
                    onClick={() => setScenesTab('overlays')}
                    className={`flex-1 text-xs py-1.5 px-3 rounded-md transition-colors ${
                      scenesTab === 'overlays'
                        ? 'bg-white/10 text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Overlays
                  </button>
                </div>

                {scenesTab === 'scenes' ? (
                  <SceneManager
                    currentScene={currentScene}
                    onSceneChange={setCurrentScene}
                    isLive={false}
                    onLayoutChange={onLayoutChange}
                  />
                ) : (
                  <OverlayManager
                    onOverlayChange={(overlays) => console.log('Overlays changed:', overlays)}
                    onOverlayToggle={(id, visible) => console.log('Overlay toggled:', id, visible)}
                  />
                )}
              </div>
            )}
          </div> */}
        </div>
      </div>

      {/* Folder Modal */}
      <ElementFolderModal
        isOpen={showFolderModal}
        onClose={() => {
          setShowFolderModal(false);
          setEditingFolder(null);
        }}
        onSave={handleSaveFolder}
        folder={editingFolder}
      />

      {/* CTA Modal */}
      <CTAModal
        isOpen={showCTAModal}
        onClose={() => {
          setShowCTAModal(false);
          setEditingCTA(null);
        }}
        onSave={handleSaveCTA}
        cta={editingCTA}
        folders={transformedFolders}
        currentFolder={transformedFolders[0]}
        readOnly={editingCTA?.readOnly === true}
        onCopyToMyElements={getSharedCopyHandler(editingCTA)}
      />

      {/* Presentation Modal */}
      <PresentationElementModal
        isOpen={showPresentationModal}
        onClose={() => {
          setShowPresentationModal(false);
          setEditingPresentation(null);
        }}
        onSave={handleSavePresentation}
        presentation={editingPresentation}
        folders={transformedFolders}
        currentFolder={transformedFolders[0]}
        readOnly={editingPresentation?.readOnly === true}
        onCopyToMyElements={getSharedCopyHandler(editingPresentation)}
      />

      {/* Poll Modal */}
      <PollModal
        isOpen={showPollModal}
        onClose={() => {
          setShowPollModal(false);
          setEditingPoll(null);
        }}
        onSave={handleSavePoll}
        poll={editingPoll}
        folders={transformedFolders}
        currentFolder={transformedFolders[0]}
        readOnly={editingPoll?.readOnly === true}
        onCopyToMyElements={getSharedCopyHandler(editingPoll)}
      />

      {/* Timer Modal */}
      <TimerElementModal
        isOpen={showTimerModal}
        onClose={() => {
          setShowTimerModal(false);
          setEditingTimer(null);
        }}
        onSave={handleSaveTimer}
        timer={editingTimer}
        folders={transformedFolders}
        currentFolder={transformedFolders[0]}
        readOnly={editingTimer?.readOnly === true}
        onCopyToMyElements={getSharedCopyHandler(editingTimer)}
      />

      {/* Video Modal */}
      <VideoElementModal
        isOpen={showVideoModal}
        onClose={() => {
          setShowVideoModal(false);
          setEditingVideo(null);
        }}
        onSave={handleSaveVideo}
        video={editingVideo}
        folders={transformedFolders}
        currentFolder={transformedFolders[0]}
        readOnly={editingVideo?.readOnly === true}
        onCopyToMyElements={getSharedCopyHandler(editingVideo)}
      />

      {/* Audio Modal */}
      <AudioElementModal
        isOpen={showAudioModal}
        onClose={() => {
          setShowAudioModal(false);
          setEditingAudio(null);
        }}
        onSave={handleSaveAudio}
        audio={editingAudio}
        folders={transformedFolders}
        currentFolder={transformedFolders[0]}
        readOnly={editingAudio?.readOnly === true}
        onCopyToMyElements={getSharedCopyHandler(editingAudio)}
      />

      {/* Prompter Modal */}
      <PrompterElementModal
        isOpen={showPrompterModal}
        onClose={() => {
          setShowPrompterModal(false);
          setEditingPrompter(null);
        }}
        onSave={handleSavePrompter}
        prompter={editingPrompter}
        folders={transformedFolders}
        currentFolder={transformedFolders[0]}
        readOnly={editingPrompter?.readOnly === true}
        onCopyToMyElements={getSharedCopyHandler(editingPrompter)}
      />

      {/* Web Overlay Modal */}
      <WebOverlayModal
        isOpen={showWebOverlayModal}
        onClose={() => {
          setShowWebOverlayModal(false);
          setEditingWebOverlay(null);
        }}
        onSave={handleSaveWebOverlay}
        overlay={editingWebOverlay}
        folders={transformedFolders}
        currentFolder={transformedFolders[0]}
        skipFolderSelect={true}
        readOnly={editingWebOverlay?.readOnly === true}
        onCopyToMyElements={getSharedCopyHandler(editingWebOverlay)}
      />

      {/* Talking Point Modal */}
      <TalkingPointModal
        isOpen={showTalkingPointModal}
        onClose={() => {
          setShowTalkingPointModal(false);
          setEditingTalkingPoint(null);
        }}
        onSave={handleSaveTalkingPoint}
        talkingPoint={editingTalkingPoint}
        folders={transformedFolders}
        currentFolder={transformedFolders[0]}
        readOnly={editingTalkingPoint?.readOnly === true}
        onCopyToMyElements={getSharedCopyHandler(editingTalkingPoint)}
      />

      {/* Lead Capture Modal */}
      <LeadCaptureModal
        isOpen={showLeadCaptureModal}
        onClose={() => {
          setShowLeadCaptureModal(false);
          setEditingLeadCapture(null);
        }}
        onSave={handleSaveLeadCapture}
        leadCapture={editingLeadCapture}
        folders={transformedFolders}
        currentFolder={transformedFolders[0]}
        readOnly={editingLeadCapture?.readOnly === true}
        onCopyToMyElements={getSharedCopyHandler(editingLeadCapture)}
      />

      {/* Product Showcase Modal */}
      <ProductShowcaseModal
        isOpen={showProductShowcaseModal}
        onClose={() => {
          setShowProductShowcaseModal(false);
          setEditingProductShowcase(null);
        }}
        onSave={handleSaveProductShowcase}
        showcase={editingProductShowcase}
        folders={transformedFolders}
        currentFolder={transformedFolders[0]}
        readOnly={editingProductShowcase?.readOnly === true}
        onCopyToMyElements={getSharedCopyHandler(editingProductShowcase)}
      />

      {/* Checkout Modal */}
      <CheckoutModal
        isOpen={showCheckoutModal}
        onClose={() => {
          setShowCheckoutModal(false);
          setEditingCheckout(null);
        }}
        onSave={handleSaveCheckout}
        checkout={editingCheckout}
        folders={transformedFolders}
        currentFolder={transformedFolders[0]}
        readOnly={editingCheckout?.readOnly === true}
        onCopyToMyElements={getSharedCopyHandler(editingCheckout)}
      />

      {/* Auction Modal */}
      <AuctionModal
        isOpen={showAuctionModal}
        onClose={() => {
          setShowAuctionModal(false);
          setEditingAuction(null);
        }}
        onSave={handleSaveAuction}
        auction={editingAuction}
        folders={transformedFolders}
        currentFolder={transformedFolders[0]}
        readOnly={editingAuction?.readOnly === true}
        onCopyToMyElements={getSharedCopyHandler(editingAuction)}
      />

      {/* Contract Modal */}
      <ContractModal
        isOpen={showContractModal}
        onClose={() => {
          setShowContractModal(false);
          setEditingContract(null);
        }}
        onSave={handleSaveContract}
        contract={editingContract}
        folders={transformedFolders}
        currentFolder={transformedFolders[0]}
        readOnly={editingContract?.readOnly === true}
        onCopyToMyElements={getSharedCopyHandler(editingContract)}
      />

      {/* Contract Renderer is rendered in MeetingContainerNew for both host and participants */}

      {/* Copy Shared Element Modal */}
      {copyingSharedElement && (
        <CopyToMyElementsModal
          isOpen={true}
          element={copyingSharedElement}
          onClose={() => setCopyingSharedElement(null)}
          onSuccess={(targetFolderId) => {
            setCopyingSharedElement(null);
            if (targetFolderId) setFocusFolderId(targetFolderId);
            onRefreshElements?.();
          }}
        />
      )}

      {/* Clone All Shared Elements Modal */}
      {cloningSharedFolder && (
        <CloneAllSharedElementsModal
          isOpen={true}
          sharedFolder={cloningSharedFolder}
          onClose={() => setCloningSharedFolder(null)}
          onSuccess={(targetFolderId) => {
            setCloningSharedFolder(null);
            if (targetFolderId) setFocusFolderId(targetFolderId);
            onRefreshElements?.();
          }}
        />
      )}

      {/* Share Element Modal */}
      {sharingElement && (
        <ShareElementModal
          isOpen={true}
          element={sharingElement}
          onClose={() => setSharingElement(null)}
        />
      )}

      {/* Share Folder Modal — same flow as Share Element but copies the
          whole folder + every element inside it on import */}
      {sharingFolder && (
        <ShareFolderModal
          isOpen={true}
          folder={sharingFolder}
          onClose={() => setSharingFolder(null)}
        />
      )}

      {/* Get Shared Element Modal */}
      {showGetSharedModal && (
        <GetSharedElementModal
          isOpen={true}
          onClose={() => setShowGetSharedModal(false)}
          onSuccess={(targetFolderId) => {
            setShowGetSharedModal(false);
            if (targetFolderId) setFocusFolderId(targetFolderId);
            onRefreshElements?.();
          }}
        />
      )}

      {/* Asset Library Modal */}
      <AssetLibraryModal
        isOpen={showAssetLibrary}
        onClose={() => setShowAssetLibrary(false)}
        onSelect={(asset) => {
          console.log('Asset selected:', asset);
          toast.success(`Selected: ${asset.name}`);
        }}
      />
    </div>
  );
}
